import AutoAwesome from "@mui/icons-material/AutoAwesome";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import Edit from "@mui/icons-material/Edit";
import Newspaper from "@mui/icons-material/Newspaper";
import Send from "@mui/icons-material/Send";
import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { useFlag } from "../../../../server/optimizely";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { sortTypes } from "../../../create-content-card/content-type-select";
import { GradientIcon } from "../../../generic/components/gradient-icon";
import { useMultiformat } from "../../../multiformat/hooks/use-multi-format";
import { useCreateSurveyHref } from "../../hooks/use-create-survey-href";
import { CreateButton } from "../create-button";
import { CreateWithAiDialog } from "../create-with-ai-dialog";

import type { RecentlyEditedType } from "../../hooks/use-recently-edited";
import type { FC, ReactNode } from "react";

/** The pencil, what to say, and Create with AI before the other ways to start. */
const EmptyState: FC<{
  heading: string;
  text: string;
  /** The format the AI dialog starts on. */
  aiFormat?: string;
  /** Prefixes each button's analytics id. */
  analyticsId: string;
  children?: ReactNode;
}> = ({ heading, text, aiFormat, analyticsId, children }) => {
  const withAiDialog = useDialog();

  return (
    <Box sx={{ textAlign: "center", py: 3 }}>
      <GradientIcon>
        <Edit sx={{ fontSize: 56 }} />
      </GradientIcon>
      <Typography variant="subtitle1" component="p" sx={{ fontWeight: 600, mt: 3, mb: 3 }}>
        {heading}
      </Typography>
      <Typography sx={{ mb: 3 }}>{text}</Typography>
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, flexWrap: "wrap" }}>
        <CreateButton
          label="Create with AI"
          icon={<AutoAwesome />}
          onClick={() => withAiDialog.handleOpen()}
          analyticsId={`${analyticsId}-with-ai`}
        />
        {children}
      </Box>
      <CreateWithAiDialog dialog={withAiDialog} defaultFormat={aiFormat} />
    </Box>
  );
};

const AllEmpty: FC = () => {
  const createSurveyHref = useCreateSurveyHref();

  return (
    <EmptyState
      heading="Nothing here yet - let’s fix that!"
      text="Create your first communication, newsletter, survey, or design, or let AI whip something up for you in seconds."
      analyticsId="dashboard-recently-edited-create"
    >
      <CreateButton
        label="Communication"
        icon={<Send />}
        href="/moments"
        analyticsId="dashboard-recently-edited-create-communication"
      />
      <CreateButton
        label="Newsletter"
        icon={<Newspaper />}
        href="/moments/newsletter"
        analyticsId="dashboard-recently-edited-create-newsletter"
      />
      <CreateButton
        label="Survey"
        icon={<BarChartRounded />}
        href={createSurveyHref}
        analyticsId="dashboard-recently-edited-create-survey"
      />
    </EmptyState>
  );
};

const CommunicationsEmpty: FC = () => (
  <EmptyState
    heading="No Communications yet - let’s fix that!"
    text="Create your first Communication, or let AI whip something up for you in seconds."
    aiFormat="Communication"
    analyticsId="dashboard-recently-edited-communications-create"
  >
    <CreateButton
      label="Create Communication from scratch"
      icon={<Send />}
      href="/moments"
      analyticsId="dashboard-recently-edited-communications-create-communication"
    />
    <CreateButton
      label="Create Newsletter from scratch"
      icon={<Newspaper />}
      href="/moments/newsletter"
      analyticsId="dashboard-recently-edited-communications-create-newsletter"
    />
  </EmptyState>
);

const SurveysEmpty: FC = () => {
  const createSurveyHref = useCreateSurveyHref();

  return (
    <EmptyState
      heading="No surveys yet - let’s fix that!"
      text="Create your first Survey, or let AI whip something up for you in seconds."
      aiFormat="Survey"
      analyticsId="dashboard-recently-edited-surveys-create"
    >
      <CreateButton
        label="Create Survey from scratch"
        icon={<BarChartRounded />}
        href={createSurveyHref}
        analyticsId="dashboard-recently-edited-surveys-create-survey"
      />
    </EmptyState>
  );
};

const DesignsEmpty: FC = () => {
  const { data: multiformat } = useMultiformat();
  const [prototypes] = useFlag("mfai_prototypes");
  // The first format after Communication in the AI dialog's own list; until the list loads, the
  // dialog starts on its usual Communication.
  const firstDesignFormat = useMemo(
    () =>
      multiformat
        ? sortTypes(multiformat, prototypes).find(({ value }) => value !== "Communication")?.value
        : undefined,
    [multiformat, prototypes],
  );

  return (
    <EmptyState
      heading="No designs yet - let’s fix that!"
      text="Create your first Design with AI."
      aiFormat={firstDesignFormat}
      analyticsId="dashboard-recently-edited-designs-create"
    />
  );
};

/** In place of the feed when there's nothing in it: a way to make the first thing of its kind. */
export const RecentlyEditedEmpty: FC<{ entityType?: RecentlyEditedType }> = ({ entityType }) => {
  switch (entityType) {
    case "communication":
      return <CommunicationsEmpty />;
    case "survey":
      return <SurveysEmpty />;
    case "digital_asset":
      return <DesignsEmpty />;
    case undefined:
    default:
      return <AllEmpty />;
  }
};
